// SEO utility functions for TradeHub24

export const SEO_CONFIG = {
  siteName: 'TradeHub24',
  siteUrl: 'https://tradehub24.co',
  defaultTitle: 'TradeHub24 - Virtual Tradeshow Platform',
  defaultDescription: 'Connect with exhibitors worldwide. Explore virtual booths, attend live tradeshows, and network without boundaries. The future of trade shows is here.',
  defaultKeywords: 'virtual tradeshow, online trade fair, virtual exhibition, B2B networking, virtual booth, trade show platform, online expo, virtual event, business networking, exhibitor platform, buyer supplier platform, virtual conference',
  twitterHandle: '@tradehub24',
  ogImage: '/tradehub_banner.png'
};

// Page-specific SEO configurations
export const PAGE_SEO = {
  home: {
    title: 'TradeHub24 - Virtual Tradeshow Platform | Connect Exhibitors & Buyers Worldwide',
    description: 'Join the future of trade shows. Browse virtual exhibitions, connect with global exhibitors, request live demos, and discover products from anywhere in the world.',
    keywords: 'virtual tradeshow, online exhibition, virtual trade fair, B2B marketplace, exhibitor platform, virtual booth, trade show online'
  },
  events: {
    title: 'Virtual Trade Shows & Exhibitions | TradeHub24',
    description: 'Browse upcoming and live virtual tradeshows. Register as an exhibitor or attendee. Discover new products and connect with industry leaders.',
    keywords: 'virtual events, online trade shows, virtual exhibitions, industry events, B2B events, trade fair calendar'
  },
  exhibitors: {
    title: 'Featured Exhibitors | TradeHub24 Virtual Tradeshow',
    description: 'Explore booths from verified exhibitors. View products, request demos, and connect with suppliers from around the world.',
    keywords: 'exhibitors, virtual booths, product showcase, supplier directory, B2B suppliers, trade show exhibitors'
  },
  calendar: {
    title: 'Event Calendar | TradeHub24 Virtual Tradeshows',
    description: 'View the complete calendar of upcoming virtual tradeshows and exhibitions. Plan your participation and never miss an industry event.',
    keywords: 'event calendar, trade show schedule, virtual event dates, exhibition calendar, upcoming tradeshows'
  },
  register: {
    title: 'Register | TradeHub24 - Join as Exhibitor or Attendee',
    description: 'Create your TradeHub24 account. Register as an exhibitor to showcase your products or as an attendee to discover new suppliers.',
    keywords: 'register, sign up, exhibitor registration, attendee registration, join tradeshow'
  }
};

// Generate structured data for search engines
export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'TradeHub24',
  description: SEO_CONFIG.defaultDescription,
  url: SEO_CONFIG.siteUrl,
  logo: `${SEO_CONFIG.siteUrl}/tradehub_logo.png`,
  sameAs: [
    'https://twitter.com/tradehub24',
    'https://linkedin.com/company/tradehub24'
  ]
});

export const generateEventSchema = (event) => ({
  '@context': 'https://schema.org',
  '@type': 'Event',
  name: event.name,
  description: event.description,
  startDate: event.start_date,
  endDate: event.end_date,
  eventStatus: 'https://schema.org/EventScheduled',
  eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
  location: {
    '@type': 'VirtualLocation',
    url: `${SEO_CONFIG.siteUrl}/events/${event.id}`
  },
  organizer: {
    '@type': 'Organization',
    name: 'TradeHub24',
    url: SEO_CONFIG.siteUrl
  },
  offers: {
    '@type': 'Offer',
    price: event.buyer_fee || 0,
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    url: `${SEO_CONFIG.siteUrl}/events/${event.id}`
  }
});

export const generateBoothSchema = (booth) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: booth.company_name,
  description: booth.description,
  url: `${SEO_CONFIG.siteUrl}/booth/${booth.id}`,
  image: booth.logo_url || booth.banner_url
});

// Generate VideoObject schema for video content (fixes "Video isn't on a watch page" GSC issue)
export const generateVideoSchema = (video) => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: video.name || video.title || 'Event Video',
  description: video.description || 'Video from TradeHub24 virtual tradeshow',
  thumbnailUrl: video.thumbnailUrl || video.poster || `${SEO_CONFIG.siteUrl}/tradehub_banner.png`,
  uploadDate: video.uploadDate || new Date().toISOString(),
  contentUrl: video.url,
  embedUrl: video.url,
  duration: video.duration || 'PT0M', // ISO 8601 duration format
  publisher: {
    '@type': 'Organization',
    name: 'TradeHub24',
    logo: {
      '@type': 'ImageObject',
      url: `${SEO_CONFIG.siteUrl}/tradehub_logo.png`
    }
  }
});

// Generate Event schema with video if present
export const generateEventWithVideoSchema = (event) => {
  const baseSchema = generateEventSchema(event);
  
  // If event has intro video, add video schema
  if (event.intro_video_url) {
    return [
      baseSchema,
      generateVideoSchema({
        name: `${event.name} - Introduction Video`,
        description: `Introduction video for ${event.name} virtual tradeshow on TradeHub24`,
        url: event.intro_video_url,
        thumbnailUrl: event.banner_url,
        uploadDate: event.created_at || new Date().toISOString()
      })
    ];
  }
  
  return baseSchema;
};

// Generate BreadcrumbList schema for better navigation in search results
export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url ? `${SEO_CONFIG.siteUrl}${item.url}` : undefined
  }))
});

// Generate WebPage schema with proper canonical URL
export const generateWebPageSchema = (page) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: page.title,
  description: page.description,
  url: `${SEO_CONFIG.siteUrl}${page.path}`,
  isPartOf: {
    '@type': 'WebSite',
    name: 'TradeHub24',
    url: SEO_CONFIG.siteUrl
  },
  ...(page.breadcrumbs && {
    breadcrumb: generateBreadcrumbSchema(page.breadcrumbs)
  })
});
